/**
 * useHardwareQrScanner — minimal version
 * ---------------------------------------------------------------------------
 * Listens for input from a USB/keyboard-wedge QR scanner and gives you back
 * the full scanned string, correctly, even on non-US keyboard layouts
 * (AZERTY, QWERTZ, etc). No external library needed.
 *
 * Usage (raw string):
 * -------------------
 * const { lastScan } = useHardwareQrScanner((raw) => {
 *   console.log(raw); // '{"type":"bon-commande","id":23569}'
 * });
 *
 * Usage (auto-parsed JSON):
 * -------------------------
 * const { lastScan } = useHardwareQrScanner(
 *   (data, raw) => {
 *     if (!data) {
 *       console.warn('Malformed scan:', raw);
 *       return;
 *     }
 *     console.log(data.type, data.id); // "bon-commande" 23569
 *   },
 *   { parseJson: true }
 * );
 * ---------------------------------------------------------------------------
 */

import { Alert, Snackbar } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// Maps a physical key (event.code, unaffected by OS keyboard layout) to the
// character it would type on a standard US layout — which is what scanners
// assume they're "typing" on. This is what keeps "-", "{", "}", etc. intact
// no matter what keyboard layout the computer is actually set to.
const KEY_MAP = {
  Minus: ['-', '_'],
  Equal: ['=', '+'],
  BracketLeft: ['[', '{'],
  BracketRight: [']', '}'],
  Backslash: ['\\', '|'],
  Semicolon: [';', ':'],
  Quote: ["'", '"'],
  Comma: [',', '<'],
  Period: ['.', '>'],
  Slash: ['/', '?'],
  Backquote: ['`', '~'],
  Space: [' ', ' '],
  Digit0: ['0', ')'],
  Digit1: ['1', '!'],
  Digit2: ['2', '@'],
  Digit3: ['3', '#'],
  Digit4: ['4', '$'],
  Digit5: ['5', '%'],
  Digit6: ['6', '^'],
  Digit7: ['7', '&'],
  Digit8: ['8', '*'],
  Digit9: ['9', '('],
};
for (let i = 0; i < 26; i++) {
  const c = String.fromCharCode(97 + i);
  KEY_MAP[`Key${c.toUpperCase()}`] = [c, c.toUpperCase()];
}

export function useHardwareQrScanner(onScan, options) {
  const enabled = options?.enabled ?? true;
  const parseJson = options?.parseJson ?? false;
  const [lastScan, setLastScan] = useState(null);

  const buffer = useRef('');
  const lastTime = useRef(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(e) {
      // Ignore normal typing in inputs/textareas so this never interferes
      // with the rest of the page.
      const target = e.target;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return;

      const now = performance.now();
      // Scanner keystrokes arrive far faster than a human can type (< 50ms
      // apart). A bigger gap means this is a new sequence, not a continuation.
      if (now - lastTime.current > 50) buffer.current = '';
      lastTime.current = now;

      if (e.key === 'Enter') {
        const value = buffer.current.trim(); // guard against stray whitespace
        buffer.current = '';
        if (value.length >= 3) {
          setLastScan(value);
          if (parseJson) {
            try {
              onScanRef.current(JSON.parse(value), value);
            } catch {
              // Malformed/incomplete scan — hand back null instead of
              // throwing, so callers can decide how to react.
              onScanRef.current(null, value);
            }
          } else {
            onScanRef.current(value, value);
          }
        }
        return;
      }

      const mapped = KEY_MAP[e.code];
      if (mapped) {
        buffer.current += e.shiftKey ? mapped[1] : mapped[0];
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, parseJson]);

  return { lastScan };
}


export const ScanFeedbackToast = ({ scanner, autoHideDuration = 2000 }) => {
  const { lastScan } = scanner;

  return (
    <Snackbar
      key={`scan-${lastScan}`} // re-triggers the toast on every new scan, even repeats after the window
      open={Boolean(lastScan)}
      autoHideDuration={autoHideDuration}
      onClose={()=>{}}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert onClose={()=>{}} severity="success" variant="filled">
        Scanned: {lastScan}
      </Alert>
    </Snackbar>
  );
};
